import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import FilterForm from '../../components/filter-form/';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { FilterOptionPropType } from '../../prop-types/filter-option';
import { GuidService } from 'ui-components/services/guid-service';
import { noop } from 'ui-components/default-props/noop';
import { TimeoutService } from '../../services/timeout-service';

import style from './filter-dropdown-menu.module.less';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

const SLIDE_ANIMATION_DURATION_IN_MS = 300;

const FilterDropdownMenu = ({
  otherFilterBys = emptyArray,
  criteriaOperatorsMap = emptyObject,
  criteriaArgumentsMap = emptyObject,
  appliedFilters = emptyArray,
  onApplyFilter = noop,
  onRemoveFilter = noop,
}) => {
  const timeoutRef = useRef();
  const [selectedFilterBy, setSelectedFilterBy] = useState();
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(true);
  const [isDropdownPushedLeft, setIsDropdownPushedLeft] = useState(false);
  const [isDropdownLeftAnimating, setIsDropdownLeftAnimating] = useState(false);
  const [isDropdownRightAnimating, setIsDropdownRightAnimating] = useState(false);
  const [shouldRenderForm, setShouldRenderForm] = useState(false);
  const [isFormPushedLeft, setIsFormPushedLeft] = useState(false);
  const [isFormLeftAnimating, setIsFormLeftAnimating] = useState(false);
  const [isFormRightAnimating, setIsFormRightAnimating] = useState(false);

  let criterionOperatorOptions = [];
  let criterionArgumentOptions = [];
  if (selectedFilterBy) {
    criterionOperatorOptions = criteriaOperatorsMap[selectedFilterBy.type];
    criterionArgumentOptions = criteriaArgumentsMap[selectedFilterBy.id];
  }

  const dropdownClassNames = classnames(
    style.dropdown,
    { [style['dropdown-push-left']]: isDropdownPushedLeft },
    { [style['dropdown-animate-left']]: isDropdownLeftAnimating },
    { [style['dropdown-animate-right']]: isDropdownRightAnimating },
  );

  const formClassNames = classnames(
    style.filterform,
    { [style['form-push-left']]: isFormPushedLeft },
    { [style['form-animate-left']]: isFormLeftAnimating },
    { [style['form-animate-right']]: isFormRightAnimating },
  );

  useEffect(() => {
    return () => {
      timeoutRef.current = undefined;
    };
  }, []);

  const onFilterBySelected = (filterBy) => {
    setSelectedFilterBy(filterBy);
    setIsDropdownLeftAnimating(true);
    setShouldRenderForm(true);
    const tId1 = setTimeout(() => {
      TimeoutService.remove(timeoutRef, tId1);
      setIsFormLeftAnimating(true);
    }, 0);
    const tId2 = setTimeout(() => {
      TimeoutService.remove(timeoutRef, tId2);
      setShouldRenderDropdown(false);
      setIsDropdownLeftAnimating(false);
      setIsDropdownPushedLeft(true);
      setIsFormLeftAnimating(false);
      setIsFormPushedLeft(true);
    }, SLIDE_ANIMATION_DURATION_IN_MS);

    TimeoutService.add(timeoutRef, tId1, tId2);
  };

  const clearFilterBySelection = () => {
    setShouldRenderForm(false);
    setSelectedFilterBy(undefined);
    setShouldRenderForm(false);
    setShouldRenderDropdown(true);
    const tId1 = setTimeout(() => {
      TimeoutService.remove(timeoutRef, tId1);
      setIsDropdownPushedLeft(false);
      setIsDropdownRightAnimating(true);
      setIsDropdownPushedLeft(false);
      setIsFormRightAnimating(true);
    }, 0);
    const tId2 = setTimeout(() => {
      TimeoutService.remove(timeoutRef, tId2);
      setIsDropdownRightAnimating(false);
      setIsFormRightAnimating(false);
    }, SLIDE_ANIMATION_DURATION_IN_MS);
  };

  const applyFilterAndCloseDropdown = useCallback(
    (newFilterBy, newCriterionOperator, newCriterionArgument, newCriterionMeta) => {
      setShouldRenderDropdown(false);
      onApplyFilter(newFilterBy, newCriterionOperator, newCriterionArgument, newCriterionMeta);
    },
    [onApplyFilter],
  );

  const removeFilterAndCloseDropdown = useCallback(
    (removedFilterIds) => {
      setShouldRenderDropdown(false);
      onRemoveFilter(removedFilterIds);
    },
    [onRemoveFilter],
  );

  return (
    <div className={style['dropdown-container']}>
      {shouldRenderDropdown && (
        <div className={dropdownClassNames}>
          {otherFilterBys
            .filter((filterBy) => !filterBy.hideWhenInactive)
            .map((filterBy) => (
              <DropdownMenuItem key={GuidService.newGuid()} onClick={() => onFilterBySelected(filterBy)}>
                {filterBy.displayName}
              </DropdownMenuItem>
            ))}
        </div>
      )}
      {shouldRenderForm && (
        <FilterForm
          appliedFilters={appliedFilters}
          className={formClassNames}
          criterionArgumentOptions={criterionArgumentOptions}
          criterionOperatorOptions={criterionOperatorOptions}
          formClassName={style['nested-dropdown-content']}
          onApplyFilter={applyFilterAndCloseDropdown}
          onBackArrowClicked={clearFilterBySelection}
          onRemoveFilter={removeFilterAndCloseDropdown}
          selectedFilterBy={selectedFilterBy}
        />
      )}
    </div>
  );
};

FilterDropdownMenu.propTypes = {
  otherFilterBys: PropTypes.arrayOf(FilterOptionPropType),
  criteriaOperatorsMap: PropTypes.object,
  criteriaArgumentsMap: PropTypes.object,
  onApplyFilter: PropTypes.func,
  onRemoveFilter: PropTypes.func,
  appliedFilters: PropTypes.array,
};

export default FilterDropdownMenu;
